import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import Dashboard from "@uppy/dashboard"
import AwsS3 from "@uppy/aws-s3"
import XHRUpload from "@uppy/xhr-upload"

const MULTIPART_THRESHOLD = 5 * 1024 * 1024 * 1024 // 5GB - S3 single upload limit

export default class extends Controller {
  static targets = ["hiddenInput", "fileName", "dashboard"]

  connect() {
    this.initializeUppy()
  }

  async initializeUppy() {
    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ["video/*"]
      }
    })

    this.uppy.use(Dashboard, {
      inline: true,
      target: this.dashboardTarget,
      hideUploadButton: true,
      proudlyDisplayPoweredByUppy: false,
    })

    // Check if S3 endpoint is available
    const useS3 = await this.checkS3Availability()

    if (useS3) {
      this.setupS3Upload()
    } else {
      this.setupDirectUpload()
    }

    this.uppy.on("upload-success", this.onUploadSuccess.bind(this));
  }

  async checkS3Availability() {
    try {
      const response = await fetch('/s3/params?filename=test.mp4&type=video%2Fmp4')
      return response.ok
    } catch {
      return false
    }
  }

  setupS3Upload() {
    this.useS3 = true
    this.uppy.use(AwsS3, {
      shouldUseMultipart: (file) => file.size > MULTIPART_THRESHOLD,

      // Single-part upload (files < 5GB)
      getUploadParameters: (file) => {
        return fetch(`/s3/params?filename=${encodeURIComponent(file.name)}&type=${encodeURIComponent(file.type)}`, {
          method: "GET",
          headers: { "Accept": "application/json" },
        }).then((response) => response.json())
        .then((data) => {
          this.uppy.setFileMeta(file.id, { uploadURL: data.fields.key });
          return data;
        });
      },

      // Multipart upload (files >= 5GB)
      createMultipartUpload: (file) => {
        return fetch("/s3/multipart", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ filename: file.name, type: file.type }),
        }).then((response) => response.json())
        .then((data) => {
          // Store the key for use in onUploadSuccess
          this.uppy.setFileMeta(file.id, { uploadURL: data.key });
          return data;
        });
      },

      listParts: (file, { uploadId, key }) => {
        return fetch(`/s3/multipart/${encodeURIComponent(uploadId)}?key=${encodeURIComponent(key)}`, {
          headers: { "Accept": "application/json" },
        }).then((response) => response.json());
      },

      signPart: (file, { uploadId, key, partNumber }) => {
        return fetch(`/s3/multipart/${encodeURIComponent(uploadId)}/${partNumber}?key=${encodeURIComponent(key)}`, {
          headers: { "Accept": "application/json" },
        }).then((response) => response.json());
      },

      completeMultipartUpload: (file, { uploadId, key, parts }) => {
        return fetch(`/s3/multipart/${encodeURIComponent(uploadId)}/complete?key=${encodeURIComponent(key)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ parts }),
        }).then((response) => response.json());
      },

      abortMultipartUpload: (file, { uploadId, key }) => {
        return fetch(`/s3/multipart/${encodeURIComponent(uploadId)}?key=${encodeURIComponent(key)}`, {
          method: "DELETE",
          headers: { "Accept": "application/json" },
        });
      },
    })
  }

  setupDirectUpload() {
    this.useS3 = false
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.uppy.use(XHRUpload, {
      endpoint: '/app/games/videos/upload',
      formData: true,
      fieldName: 'file',
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
  }

  onUploadSuccess(file, response) {
    let uploadedFileData

    if (this.useS3) {
      // Extract key from upload URL (works for both single-part and multipart)
      const uploadURL = file.meta.uploadURL
      const match = uploadURL.match(/attachments_cache\/(.+)/)
      uploadedFileData = {
        id: match[1],
        storage: "cache",
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        }
      }
    } else {
      // Direct upload - get from server response
      uploadedFileData = response.body
    }

    this.hiddenInputTarget.value = JSON.stringify(uploadedFileData);
    this.fileNameTarget.textContent = file.name;
  }

  disconnect() {
    if (this.uppy) {
      this.uppy.destroy();
      this.uppy = null;
    }
  }
}
